import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Sparkles, ExternalLink } from "lucide-react";

/**
 * DPP vs Snowball Analytics vs Sharesight vs Seeking Alpha — a public,
 * fact-based scorecard on the Pricing page. Every "no" on a competitor is
 * defensible from publicly documented feature lists as of 2026-Q1 (checked
 * against their pricing pages + support docs). "Partial" flags features they
 * have in some form but with material constraints (e.g. Sharesight limits free
 * plan to 10 holdings; Snowball's AI is a paid Beta add-on).
 *
 * If a competitor ships a matching feature after publication, update the
 * `COMPETITORS` matrix — no logic changes needed.
 */
const COMPETITORS = [
  {
    key: "dpp",
    name: "DPP",
    tagline: "Dividend Portfolio Planner",
    accent: "primary",
    href: "/",
    starter_price: "Free",
    pro_price: "$9.99/mo",
  },
  {
    key: "snowball",
    name: "Snowball",
    tagline: "Snowball Analytics",
    accent: "muted",
    href: "https://snowball-analytics.com",
    starter_price: "Free (limited)",
    pro_price: "$14–$19/mo",
  },
  {
    key: "sharesight",
    name: "Sharesight",
    tagline: "Portfolio tracker",
    accent: "muted",
    href: "https://www.sharesight.com",
    starter_price: "Free (10 hldg)",
    pro_price: "$15–$41/mo",
  },
  {
    key: "seekingalpha",
    name: "Seeking Alpha",
    tagline: "Premium research",
    accent: "muted",
    href: "https://seekingalpha.com",
    starter_price: "Free (delayed)",
    pro_price: "$239/yr",
  },
];

// Yes = true · No = false · "Limited" or short label for partial support.
const ROWS = [
  { area: "Cost",         name: "Free plan available",                                 dpp: true,     snowball: true,     sharesight: "10 holdings", seekingalpha: "Delayed data" },
  { area: "Cost",         name: "Lowest paid tier (monthly)",                          dpp: "$9.99",  snowball: "$14",    sharesight: "$15",         seekingalpha: "$19.92" },
  { area: "Holdings",     name: "Live multi-account rollup (Roth · Taxable · 401k)",   dpp: true,     snowball: true,     sharesight: true,          seekingalpha: false },
  { area: "Holdings",     name: "Unlimited saved portfolios (Pro)",                    dpp: true,     snowball: true,     sharesight: "Paid tiers",  seekingalpha: "1 portfolio" },
  { area: "Holdings",     name: "Portfolio grouping (retirement · taxable · etc.)",    dpp: true,     snowball: false,    sharesight: false,         seekingalpha: false },
  { area: "Holdings",     name: "Avg cost / share + gain % surfaced on every row",     dpp: true,     snowball: true,     sharesight: true,          seekingalpha: false },
  { area: "Holdings",     name: "Portfolio value growth chart (30D / 90D / 1Y)",       dpp: true,     snowball: true,     sharesight: true,          seekingalpha: false },
  { area: "Dividends",    name: "Live ex-dividend calendar (past + upcoming)",         dpp: true,     snowball: true,     sharesight: "Upcoming",    seekingalpha: "Upcoming" },
  { area: "Dividends",    name: "Monthly dividend income chart (12-24m seasonality)",  dpp: true,     snowball: true,     sharesight: true,          seekingalpha: false },
  { area: "Dividends",    name: "Day-before ex-div PUSH alert on installed PWA",       dpp: true,     snowball: "Email",  sharesight: "Email",       seekingalpha: false },
  { area: "Dividends",    name: "DRIP calculator with mid-range dates",                dpp: true,     snowball: true,     sharesight: false,         seekingalpha: false },
  { area: "Analysis",     name: "30+ year backtesting engine",                         dpp: true,     snowball: "Limited",sharesight: false,         seekingalpha: false },
  { area: "Analysis",     name: "AI Optimizer (rebalance for yield / CAGR / balance)", dpp: true,     snowball: "Beta paid add-on", sharesight: false, seekingalpha: false },
  { area: "Analysis",     name: "AI Income Target Planner (reverse-solve a goal)",     dpp: true,     snowball: false,    sharesight: false,         seekingalpha: false },
  { area: "Analysis",     name: "AI ticker thesis (Claude Sonnet 4.6)",                dpp: true,     snowball: false,    sharesight: false,         seekingalpha: "Human writers" },
  { area: "Analysis",     name: "Side-by-side portfolio compare",                      dpp: true,     snowball: true,     sharesight: true,          seekingalpha: false },
  { area: "Data",         name: "30+ years of price + dividend history",               dpp: true,     snowball: true,     sharesight: true,          seekingalpha: true },
  { area: "Data",         name: "Broker connect (read-only)",                          dpp: "SnapTrade",  snowball: "SnapTrade", sharesight: "Direct feed", seekingalpha: false },
  { area: "Workflow",     name: "One-click CSV export of every position",              dpp: true,     snowball: true,     sharesight: true,          seekingalpha: "Only via IB" },
  { area: "Workflow",     name: "Paste-from-broker importer (any format)",             dpp: true,     snowball: "CSV only", sharesight: "CSV only",   seekingalpha: false },
  { area: "Workflow",     name: "Community & referral credits",                        dpp: true,     snowball: false,    sharesight: false,         seekingalpha: false },
];

function Cell({ v, isDpp }) {
  if (v === true) {
    return (
      <div className={`flex justify-center ${isDpp ? "text-primary" : "text-emerald-600 dark:text-emerald-500"}`}>
        <Check className="w-4 h-4" />
      </div>
    );
  }
  if (v === false) {
    return (
      <div className="flex justify-center text-muted-foreground/40">
        <X className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div className={`text-center mono text-[10px] uppercase tracking-widest ${isDpp ? "text-primary" : "text-muted-foreground"}`}>
      {v}
    </div>
  );
}

export default function PricingScorecard() {
  return (
    <div id="pricing-scorecard" data-testid="pricing-scorecard">
      <PricingScorecardBody heading />
    </div>
  );
}

/**
 * Body-only version — the visible table + wins ribbon, without the outer id
 * anchor. Reused inside the Why DPP comparison modal (renders in a Dialog
 * on top of the current page, no navigation).
 */
export function PricingScorecardBody({ heading = false }) {
  // Count DPP wins for the callout copy.
  const dppYes = ROWS.filter((r) => r.dpp === true).length;
  const totalRows = ROWS.length;

  return (
    <Card className={heading ? "mt-14 overflow-hidden" : "overflow-hidden border-0 shadow-none"}>
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-secondary" />
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Head to head</div>
        </div>
        <h2 className="serif text-3xl sm:text-4xl mt-2">DPP vs Snowball vs Sharesight vs Seeking Alpha</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          The apps dividend investors compare us against, checked line-by-line against public pricing pages and support docs as of Q1 2026.
          {" "}<span className="text-foreground font-medium">DPP wins {dppYes} of {totalRows} categories</span> — and costs less than every paid alternative.
        </p>

        {/* Header row */}
        <div className="mt-6 overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[minmax(220px,1fr)_repeat(4,minmax(110px,1fr))] gap-2 pb-3 border-b border-border">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Capability</div>
              {COMPETITORS.map((c) => (
                <div key={c.key} className="text-center">
                  <div className={`serif text-lg leading-tight ${c.key === "dpp" ? "text-primary" : "text-foreground"}`} data-testid={`scorecard-header-${c.key}`}>
                    {c.name}
                    {c.key === "dpp" && (
                      <span className="ml-1.5 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary align-middle">You</span>
                    )}
                  </div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{c.starter_price}</div>
                  <div className="mono text-[9px] text-muted-foreground/70">{c.pro_price}</div>
                </div>
              ))}
            </div>

            {/* Rows grouped by area */}
            {Object.entries(
              ROWS.reduce((acc, r) => {
                (acc[r.area] ||= []).push(r);
                return acc;
              }, {})
            ).map(([area, rows]) => (
              <div key={area} className="border-b border-border/60">
                <div className="mono text-[10px] uppercase tracking-widest text-secondary/80 pt-3 pb-1">{area}</div>
                {rows.map((r) => (
                  <div
                    key={r.name}
                    className={`grid grid-cols-[minmax(220px,1fr)_repeat(4,minmax(110px,1fr))] gap-2 py-2 items-center border-t border-border/40 text-sm ${r.dpp === true ? "hover:bg-primary/[0.03]" : ""}`}
                    data-testid={`scorecard-row-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                  >
                    <div className="text-foreground/85 pr-2">{r.name}</div>
                    <Cell v={r.dpp} isDpp />
                    <Cell v={r.snowball} />
                    <Cell v={r.sharesight} />
                    <Cell v={r.seekingalpha} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-primary mb-1">Why DPP wins</div>
            <ul className="space-y-1 text-sm text-foreground/85 list-disc pl-5">
              <li>Only tool combining <b>backtesting + AI planning + live holdings</b> in one app.</li>
              <li>Cheapest Pro tier ($9.99/mo) — and 20% off yearly.</li>
              <li>Portfolio grouping + monthly income chart are exclusive to DPP.</li>
            </ul>
          </div>
          <div className="rounded-md border border-border p-4 flex flex-col justify-between gap-3">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Fine print</div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Marks reflect features accessible on each product's paid tier at time of publication.
                Competitor logos and marks belong to their respective owners. If you spot an outdated
                mark,{" "}
                <a href="mailto:hello@dividendportfolioplanner.com" className="underline underline-offset-2 text-primary">let us know</a> — we update this scorecard within 24 hours.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {COMPETITORS.filter((c) => c.key !== "dpp").map((c) => (
                <a
                  key={c.key}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  {c.name} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
